import * as React from 'react';
import * as autoBind from 'react-autobind';
import Constants from '../../globals/Constants';
import Square from '../AnnotationShapes/Square';
import Arrow from '../AnnotationShapes/Arrow';
import TimeFormatter from '../../utils/TimeFormatter/TimeFormatter';
import CommentModel from '../../types/CommentModel';
import MediaDimensionsModel from '../../types/MediaDimensionsModel';
import {AnnotationModes, QuizState} from '../../globals/Constants';
import IVideoPlayer from '../../types/IVideoPlayer';
import {fabric} from 'fabric-webpack';
import Strings from '../../globals/Strings';

export default class Annotation extends React.Component<AnnotationProps, {}> {
   pointOfOrigin: 'center';
   canvas: any;
   doubleClickTimeoutId: number;
   doubleClickDelay: number = 300;
   doubleClickTimeoutIdDefault = -1;
   canvasRef: any;

   constructor(props: AnnotationProps) {
      super(props);
      autoBind(this);

      this.doubleClickTimeoutId = this.doubleClickTimeoutIdDefault;
   }

   resetCenter(item: any): void {
      if (!item.getPointByOrigin) {
         return;
      }

      const coords = item.getPointByOrigin(this.pointOfOrigin, this.pointOfOrigin);
      item.left = coords.x;
      item.top = coords.y;
      item.originX = this.pointOfOrigin;
      item.originY = this.pointOfOrigin;
   }

   lockAnnotations(): void {
      this.canvas.getObjects().forEach((item: any) => {
         item.lockMovementX = true;
         item.lockMovementY = true;
         item.lockRotation = true;
         item.lockScalingX = true;
         item.lockScalingY = true;
         item.hasBorders = false;
         item.hasControls = false;
         item.hoverCursor = Constants.cursors.default;

         if (item.animate) {
            this.resetCenter(item);
            const origScaleX = item.scaleX;
            const origScaleY = item.scaleY;
            item.scaleX = item.scaleX * Constants.annotation.preloadAnnotationScale;
            item.scaleY = item.scaleY * Constants.annotation.preloadAnnotationScale;
            item.opacity = 0;
            item.animate({opacity: 1, scaleX: origScaleX, scaleY: origScaleY}, {
               duration: Constants.annotation.animationDuration,
               onChange: this.canvas.renderAll.bind(this.canvas)
            });
         }
      });
   }

   loadAnnotations(): void {
      const {setAnnotationMode, selectedComment, hideAnnotation, player, playTime, visibleComments} = this.props;

      this.clearCanvas();
      setAnnotationMode(AnnotationModes.none);
      this.canvas.isDrawingMode = false;

      const parentComment = selectedComment.parentCommentId && visibleComments ? visibleComments.find(c => c.id === selectedComment.parentCommentId) : selectedComment;

      if (!parentComment.annotation || hideAnnotation || !player.getIsPaused()) {
         return;
      }

      const timeDifference = TimeFormatter.getRoundedTime(Math.abs(playTime - selectedComment.time));
      const shouldLoadAnnotation = timeDifference <= Constants.annotation.timeTolerance;

      if (shouldLoadAnnotation) {
         setAnnotationMode(AnnotationModes.display);
         this.canvas.loadFromJSON(parentComment.annotation);
         this.lockAnnotations();
         this.rescale();
      }
   }

   saveCanvasChange(): void {
      const {storeAnnotation} = this.props;
      this.removeOutOfViewObjects();
      storeAnnotation(this.canvas.isEmpty() ? '' : this.canvas.toDatalessJSON());
   }

   componentDidUpdate(prevProps: AnnotationProps): void {
      const {mediaDimensions, annotationMode, playTime, selectedComment, hideAnnotation} = this.props;
      const hasWidthChanged = mediaDimensions.width !== prevProps.mediaDimensions.width;
      const hasHeightChanged = mediaDimensions.height !== prevProps.mediaDimensions.height;
      const hasAnnotationModeChanged = annotationMode !== prevProps.annotationMode;
      const hasPlayTimeChanged = playTime !== prevProps.playTime;
      const hasSelectAnnotationChanged = selectedComment.annotation !== prevProps.selectedComment.annotation;
      const annotationModeIsEdit = annotationMode === AnnotationModes.edit;
      const loadBecauseModeChange = hasAnnotationModeChanged && annotationMode === AnnotationModes.none;
      const loadBecausePlayTimeOrSelectAnnotation = !hasAnnotationModeChanged && !annotationModeIsEdit && (hasPlayTimeChanged || hasSelectAnnotationChanged);
      const loadBecauseHideAnnotationChange = !hasAnnotationModeChanged && hideAnnotation !== prevProps.hideAnnotation;

      if (annotationMode !== prevProps.annotationMode && prevProps.annotationMode === AnnotationModes.edit) {
         this.canvas.off('mouse:down');
         this.canvas.off('mouse:move');
         this.canvas.off('mouse:up');
      }

      if (hasWidthChanged || hasHeightChanged) {
         this.canvas.setDimensions({width: mediaDimensions.width, height: mediaDimensions.height});
         this.rescale();
      }

      if (hasAnnotationModeChanged) {
         this.canvas.selection = annotationModeIsEdit;
         if (annotationModeIsEdit) {
            this.clearCanvas();
         }
      }

      if (loadBecauseModeChange || loadBecausePlayTimeOrSelectAnnotation || loadBecauseHideAnnotationChange) {
         this.loadAnnotations();
      }

      this.canvas.renderAll();
   }

   onColorChange(color: string): void {
      this.canvas.freeDrawingBrush.color = color;

      const selectedShape = this.canvas.getActiveObject();
      const selectedShapes = this.canvas.getActiveGroup();

      if (selectedShape) {
         this.changeShapeColor(selectedShape, color);
      }

      if (selectedShapes) {
         selectedShapes._objects.forEach((shape: any) => {
            this.changeShapeColor(shape, color);
         });
      }

      this.canvas.renderAll();
      this.canvas.trigger('object:modified', {target: {}});
   }

   changeShapeColor(shape: any, color: string): void {
      shape.stroke = color;

      if (shape.fill !== Constants.annotation.transparentColor) {
         shape.fill = color;
      }
   }

   rescale(): void {
      const {mediaDimensions} = this.props;

      const scaleRatio = Math.max(mediaDimensions.width, mediaDimensions.height) / Constants.annotation.scale;
      if (scaleRatio <= 0) {
         return;
      }

      this.canvas.setZoom(scaleRatio);
      this.canvas.strokeScale = Constants.annotation.scale / Math.max(mediaDimensions.width, mediaDimensions.height);
      const strokeWidth = this.canvas.strokeScale * Constants.annotation.strokeWidth;
      this.canvas.freeDrawingBrush.width = strokeWidth;
      this.canvas.getObjects().forEach((obj: any) => {
         obj.strokeWidth = strokeWidth;
      });
   }

   onSelectionCreated(e: any): void {
      if (e.target.type === 'group') {
         e.target.hasControls = false;
      }
   }

   componentDidMount(): void {
      const {mediaDimensions, annotationMode, setCanvas, player} = this.props;

      this.canvas = new fabric.Canvas('canvas', {
         width: mediaDimensions.width,
         height: mediaDimensions.height,
         uniScaleTransform: true
      });

      this.rescale();
      this.canvas.selection = annotationMode === AnnotationModes.edit;
      this.canvas.on('colorchange', this.onColorChange);
      this.canvas.on('object:added', this.saveCanvasChange);
      this.canvas.on('object:removed', this.saveCanvasChange);
      this.canvas.on('object:modified', this.saveCanvasChange);
      this.canvas.on('selection:created', this.onSelectionCreated);
      this.canvasRef.addEventListener('touchstart', this.onTouchStart, {passive: false});
      window.addEventListener('keydown', this.onKeyDown);
      Square.initializeEventListeners(this.canvas);
      Arrow.initializeEventListeners(this.canvas);
      player.on('ended', this.loadAnnotations);

      if (annotationMode !== AnnotationModes.edit) {
         this.loadAnnotations();
      }

      setCanvas(this.canvas);
   }

   isObjectOffCanvas(top: number, left: number, bottom: number, right: number): boolean {
      const offset = Constants.annotation.controlsPadding;
      const pastLeft = right < offset;
      const pastTop = bottom < offset;
      const pastRight = left > this.canvas.width - offset;
      const pastBottom = top > this.canvas.height - offset;
      return pastLeft || pastTop || pastRight || pastBottom;
   }

   removeOutOfViewObjects(): void {
      // Remove objects inside the selection if they are outside the canvas
      const activeGroup = this.canvas.getActiveGroup();
      if (activeGroup) {
         activeGroup._objects.forEach((shape: any) => {
            const bounds = shape.getBoundingRect();
            const groupBounds = shape.group.getBoundingRect();
            // Canvas group objects are offset by the group dimensions
            const groupOffsetTop = groupBounds.height / 2;
            const groupOffsetLeft = groupBounds.width / 2;
            const top = bounds.top + groupBounds.top + groupOffsetTop;
            const left = bounds.left + groupBounds.left + groupOffsetLeft;
            const bottom = top + bounds.height;
            const right = left + bounds.width;

            if (this.isObjectOffCanvas(top, left, bottom, right)) {
               shape.group.removeWithUpdate(shape); // Updates the selection border after removing the object
               this.canvas.remove(shape);
            }
         });
      }

      // Remove shapes outside a selection if they are outside the canvas
      this.canvas.getObjects().forEach((shapes: any) => {
         const bounds = shapes.getBoundingRect();
         if (!shapes.group && this.isObjectOffCanvas(bounds.top, bounds.left, bounds.top + bounds.height, bounds.left + bounds.width)) {
            this.canvas.remove(shapes);
         }
      });
   }

   clearCanvas(): void {
      this.canvas.clear();
      this.props.storeAnnotation('');
   }

   componentWillUnmount(): void {
      this.props.setQuizState(QuizState.closed);
      window.removeEventListener('keydown', this.onKeyDown);
      this.canvasRef.removeEventListener('touchstart', this.onTouchStart);
      this.canvas.dispose();
   }

   onClick(e: any): void {
      const {annotationMode, player} = this.props;
      e.stopPropagation();

      if (annotationMode !== AnnotationModes.edit) {
         if (this.doubleClickTimeoutId !== this.doubleClickTimeoutIdDefault) {
            clearTimeout(this.doubleClickTimeoutId);
            this.doubleClickTimeoutId = this.doubleClickTimeoutIdDefault;
         } else {
            this.doubleClickTimeoutId = setTimeout(() => {
               this.doubleClickTimeoutId = -1;
               player.getIsPaused() ? player.play() : player.pause();
            }, this.doubleClickDelay) as any;
         }
      }
   }

   onKeyDown(e: any): void {
      const isEditAnnotationMode = this.props.annotationMode === AnnotationModes.edit;
      const isDeleteOrBackspaceEvent = e.keyCode === Constants.keys.backspace || e.keyCode === Constants.keys.delete;
      const editingInputTypes = [Constants.elementTypes.input, Constants.elementTypes.textArea, Constants.elementTypes.search];
      const isEditingText = editingInputTypes.some(type => e.target.type === type);

      if (!isEditAnnotationMode || !isDeleteOrBackspaceEvent || isEditingText) {
         return;
      }

      const activeObject = this.canvas.getActiveObject();
      const activeGroup = this.canvas.getActiveGroup();

      if (!activeObject && !activeGroup) {
         return;
      }

      e.preventDefault(); // Do not trigger navigation back if we are deleting an object
      if (activeGroup) {
         activeGroup._objects.forEach((shape: any) => {
            this.canvas.remove(shape);
         });
      }

      if (activeObject) {
         this.canvas.remove(activeObject);
      }

      this.canvas.deactivateAll();
      this.canvas.renderAll();
   }

   onTouchStart(e: any) {
      if (this.props.annotationMode === AnnotationModes.edit) {
         e.preventDefault();
      }
   }

   setCanvasRef(ref: any) {
      this.canvasRef = ref;
   }

   render() {
      const {mediaDimensions, center, annotationMode, quizState} = this.props;
      const style: any = {
         width: mediaDimensions.width,
         height: mediaDimensions.height,
         pointerEvents: annotationMode === AnnotationModes.edit ? 'auto' : 'none'
      };

      if (center) {
         style.position = 'absolute';
         style.left = 0;
         style.right = 0;
         style.top = 0;
         style.bottom = 0;
      }

      return (
         <React.Fragment>
            <div ref={this.setCanvasRef} className="canvas" style={style} onClick={this.onClick}>
               <canvas id="canvas"/>
            </div>
            {annotationMode === AnnotationModes.edit && <div className="controls-hidden-mode">
               <div className="controls-hidden-mode-header">{Strings.labels.annotations.annotationModeHeader}</div>
               <div>{Strings.labels.annotations.annotationModeText}</div>
            </div>}
            {quizState !== QuizState.closed && <div className="controls-hidden-mode">
               <div className="controls-hidden-mode-header">{Strings.labels.quizzing.quizzingModeHeader}</div>
               <div>{quizState === QuizState.setupOpen ? Strings.labels.quizzing.setupModeText : Strings.labels.quizzing.quizzingModeText}</div>
            </div>}
         </React.Fragment>
      );
   }
}

export interface AnnotationProps {
   selectedComment: CommentModel;
   mediaDimensions: MediaDimensionsModel;
   player: IVideoPlayer;
   playTime: number;
   annotationMode: AnnotationModes;
   hideAnnotation: boolean;
   center: boolean;
   quizState: QuizState;
   visibleComments: CommentModel[];
   storeAnnotation(annotation: string): void;
   setCanvas(canvas: any): void;
   setAnnotationMode(annotationMode: AnnotationModes): void;
   setQuizState(quizState: QuizState): void;
}
